// src/app/create-quote.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
  ScrollView,
  Platform,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MdAdd, MdRemove } from 'react-icons/md';
import API_BASE_URL from '../apiConfig.js';

// --- helpers ---
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

export default function CreateQuote() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // customer selection
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerName, setCustomerName] = useState('');

  // quote data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);

  // add-customer inline form (web-friendly replacement for RN Modal)
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    notes: '', // Add notes field
  });

  // preview overlay (instead of WebView/Modal)
  const [previewOpen, setPreviewOpen] = useState(false);

  // user permissions
  const [userPermissions, setUserPermissions] = useState(null);

  // Custom Modal Component for better-looking dialogs (like quotes.jsx)
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  // Email state for sending quotes
  const [sendingEmail, setSendingEmail] = useState(false);
  const [lastCreatedQuoteId, setLastCreatedQuoteId] = useState(null);
  const [previewQuoteData, setPreviewQuoteData] = useState({});
  const [businessInfo, setBusinessInfo] = useState({}); // Add business info state

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({ open: true, title, message, buttons });
  };

  const closeCustomModal = () => {
    setCustomModal({ open: false, title: '', message: '', buttons: [] });
  };

  // read URL params (expo-router replacement)
  useEffect(() => {
    const pTitle = searchParams.get('title');
    const pDesc = searchParams.get('description');
    const pPrice = searchParams.get('price');
    const pCustomerName = searchParams.get('customerName');

    if (pTitle) setTitle(pTitle);
    if (pDesc) setDescription(pDesc);
    if (pPrice) setPrice(String(pPrice));
    if (pCustomerName) {
      setCustomerName(pCustomerName);
      setSearchQuery(pCustomerName);
      setSelectedCustomer(pCustomerName);
    }
  }, [searchParams]);

  // load customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API_BASE_URL}/customers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const list = Array.isArray(data) ? data : data?.customers ?? [];
        setCustomers(list);

        const pCustomerName = searchParams.get('customerName');
        if (pCustomerName && list.some((c) => c.name === pCustomerName)) {
          setFilteredCustomers([]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };
    fetchCustomers();
  }, [searchParams]);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          // Check if user has quote permissions
          if (parsedPermissions?.quote_perm === 'none') {
            alert("You don't have permission to create quotes. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const t = q.toLowerCase();
    const matches = customers.filter((c) => (c?.name ?? '').toLowerCase().includes(t));
    setFilteredCustomers(matches);
  };

  const handleAddCustomer = async () => {
    try {
      const token = getToken();
      console.log('🚀 Creating customer with data:', newCustomer);
      
      // Ensure notes field is included (even if empty)
      const customerPayload = {
        ...newCustomer,
        notes: newCustomer.notes || '', // Add notes field
      };
      
      const { data } = await axios.post(`${API_BASE_URL}/customers/returnId`, customerPayload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      console.log('✅ Customer creation response:', data);

      const added = data;
      const updated = [...customers, added];
      setCustomers(updated);

      setCustomerName(newCustomer.name);
      setSearchQuery(newCustomer.name);
      setSelectedCustomer(newCustomer.name);
      setFilteredCustomers([]);
      setAddingCustomer(false);
      
      // Reset the newCustomer form including notes
      setNewCustomer({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        notes: '', // Reset notes field
      });
      
      showCustomModal(
        'Customer Added Successfully! ✅',
        `${added.name} has been added to your customer list and is now selected.`,
        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
      );
    } catch (error) {
      console.error('Error adding customer:', error);
      console.error('Customer creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to add customer. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create customers.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid customer data format. Please check all required fields are filled.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  const saveQuote = async () => {
    const chosen = customers.find(
      (c) => (c?.name ?? '').trim().toLowerCase() === (selectedCustomer || searchQuery).trim().toLowerCase()
    );
    if (!chosen) {
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    // Ensure price defaults to "0.00" if blank or invalid
    let cleanedPrice = price && price.trim() !== '' ? price.trim() : '0.00';
    
    // Validate and format price
    if (cleanedPrice === '' || isNaN(parseFloat(cleanedPrice))) {
      cleanedPrice = '0.00';
    } else {
      // Ensure proper decimal formatting
      const numPrice = parseFloat(cleanedPrice);
      cleanedPrice = numPrice.toFixed(2);
    }

    const newQuote = {
      customer: chosen.name,
      customerId: chosen.id,
      title,
      description,
      price: cleanedPrice,
      notes,
    };

    const paramId = searchParams.get('id');
    if (paramId) newQuote.id = paramId;

    // Store quote data for preview modal before clearing form
    const quotePreviewData = {
      customerName: chosen.name,
      title,
      description,
      price: cleanedPrice,
      notes,
    };

    try {
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/quotes`, newQuote, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      console.log('✅ Quote creation response:', response.data);

      const quoteId = response.data?.id;
      const businessData = response.data?.businessInfo; // Capture business info from backend

      // Store business info for preview
      if (businessData) {
        setBusinessInfo(businessData);
      }

      if (!quoteId) {
        console.error('❌ No quote ID returned from backend');
        showCustomModal(
          'Warning',
          'Quote was created but ID was not returned. Please try emailing from the quotes list.',
          [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
        );
        // Clear form and redirect
        setTitle('');
        setDescription('');
        setPrice('');
        setNotes('');
        setSearchQuery('');
        setSelectedCustomer('');
        setCustomerName('');
        setFilteredCustomers([]);
        setIsNotesVisible(false);
        setAddingCustomer(false);
        navigate('/quotes', { replace: true });
        return;
      }

      console.log('📧 Quote created successfully with ID:', quoteId);
      setLastCreatedQuoteId(quoteId);
      setPreviewQuoteData(quotePreviewData);

      // Clear form
      setTitle('');
      setDescription('');
      setPrice('');
      setNotes('');
      setSearchQuery('');
      setSelectedCustomer('');
      setCustomerName('');
      setFilteredCustomers([]);
      setIsNotesVisible(false);
      setAddingCustomer(false);

      // Email quote functionality with proper state management
      const emailQuotePDF = async () => {
        if (sendingEmail) return;
        
        if (!chosen?.email) {
          showCustomModal(
            'Email Not Available ⚠️',
            `Customer ${chosen?.name || quotePreviewData.customerName} does not have an email address on file. Please update customer information to enable emailing.`,
            [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
          );
          return;
        }

        try {
          setSendingEmail(true);
          
          // Update the modal to show "Sending..." state
          showCustomModal(
            'Quote Created Successfully! 🎉',
            'Your quote has been created. What would you like to do next?',
            [
              {
                text: 'Sending…',
                style: 'primary',
                disabled: true,
                onPress: () => {} // Do nothing while sending
              },
              {
                text: '👁️ Preview Quote',
                style: 'secondary',
                onPress: () => {
                  closeCustomModal();
                  setPreviewOpen(true);
                }
              },
              {
                text: '📋 View Quotes',
                style: 'default',
                onPress: () => {
                  closeCustomModal();
                  navigate('/quotes', { replace: true });
                }
              }
            ]
          );

          console.log(`📧 Attempting to email quote ${quoteId} to customer...`);
          
          const emailResponse = await axios.post(`${API_BASE_URL}/quotes/${quoteId}/email`, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });

          console.log('Email response:', emailResponse.data);
          
          if (emailResponse.data && emailResponse.data.success) {
            showCustomModal(
              'Email Sent Successfully! ✅',
              `Quote has been emailed as PDF to ${chosen.email}`,
              [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
            );
          } else {
            showCustomModal(
              'Email Status Unclear ⚠️',
              emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
              [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
            );
          }
        } catch (emailError) {
          console.error('Error sending quote email:', emailError);
          console.error('Email error response:', emailError.response?.data);
          
          let errorMessage = 'Failed to send email. Please try again.';
          
          if (emailError.response?.status === 400) {
            errorMessage = `Customer email issue: ${chosen.email ? 'Email may be invalid' : 'No email address on file'}. Please update customer information.`;
          } else if (emailError.response?.status === 404) {
            errorMessage = 'Quote not found. Please try creating the quote again.';
          } else if (emailError.response?.data?.detail) {
            errorMessage = emailError.response.data.detail;
          } else if (emailError.response?.data?.message) {
            errorMessage = emailError.response.data.message;
          } else if (emailError.message) {
            errorMessage = `Network error: ${emailError.message}`;
          }
          
          showCustomModal(
            'Email Failed ❌',
            errorMessage,
            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
          );
        } finally {
          setSendingEmail(false);
        }
      };

      // Show beautiful three-option modal matching other pages
      showCustomModal(
        'Quote Created Successfully! 🎉',
        'Your quote has been created. What would you like to do next?',
        [
          {
            text: '📧 Email PDF to Customer',
            style: 'primary',
            onPress: emailQuotePDF
          },
          {
            text: '👁️ Preview Quote',
            style: 'secondary',
            onPress: () => {
              closeCustomModal();
              setPreviewOpen(true);
            }
          },
          {
            text: '📋 View Quotes',
            style: 'default',
            onPress: () => {
              closeCustomModal();
              navigate('/quotes', { replace: true });
            }
          }
        ]
      );
      
    } catch (error) {
      console.error('Error saving quote:', error);
      console.error('Quote creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to save quote.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid quote data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create quotes.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ minHeight: '100vh', backgroundColor: '#fff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
          <View style={styles.container}>

            {/* Customer search/select */}
            <Text style={styles.label}>Customer Name</Text>
            <TextInput
              style={styles.input}
              value={searchQuery}
              onChangeText={handleSearch}
              onBlur={() => {
                if (customers.some((c) => c.name === searchQuery)) setFilteredCustomers([]);
              }}
              placeholder="Search or add customer"
            />
            {searchQuery ? (
              <View style={styles.searchResults}>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer, index) => (
                    <TouchableOpacity
                      key={customer.id || `c-${index}`}
                      onPress={() => {
                        setCustomerName(customer.name);
                        setSearchQuery(customer.name);
                        setSelectedCustomer(customer.name);
                        setFilteredCustomers([]);
                      }}
                    >
                      <Text style={styles.searchResult}>{customer.name}</Text>
                    </TouchableOpacity>
                  ))
                ) : !customers.some((c) => c.name === searchQuery) ? (
                  <View style={styles.addCustomerRow}>
                    <Text style={styles.searchResult}>{searchQuery}</Text>
                    <TouchableOpacity
                      style={styles.addCustomerButton}
                      onPress={() => {
                        setNewCustomer((nc) => ({ ...nc, name: searchQuery }));
                        setAddingCustomer(true);
                      }}
                    >
                      <MdAdd size={20} color="green" />
                      <Text style={styles.addCustomerText}>Add Customer</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* Inline "Add Customer" form */}
            {addingCustomer && (
              <View style={styles.inlineModal}>
                <Text style={styles.modalHeader}>Add Customer</Text>
                {[
                  ['Customer Name', 'name'],
                  ['Address', 'address'],
                  ['City', 'city'],
                  ['State', 'state'],
                  ['Zip', 'zip'],
                  ['Phone', 'phone'],
                  ['Email', 'email'],
                  ['Notes', 'notes'], // Add notes field
                ].map(([label, key]) => (
                  <View key={key} style={{ marginBottom: 10 }}>
                    <Text style={styles.modalLabel}>{label}</Text>
                    <TextInput
                      style={styles.input}
                      value={newCustomer[key]}
                      onChangeText={(t) => setNewCustomer((nc) => ({ ...nc, [key]: t }))}
                      placeholder={label}
                    />
                  </View>
                ))}
                <TouchableOpacity style={styles.saveButton} onPress={handleAddCustomer}>
                  <Text style={styles.saveButtonText}>Save Customer</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.returnButton} onPress={() => setAddingCustomer(false)}>
                  <Text style={styles.returnButtonText}>Return To Quote</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Quote fields */}
            <Text style={styles.label}>Title</Text>
            <TextInput style={styles.input} value={title} onChangeText={setTitle} />

            <Text style={styles.label}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              multiline
            />

            <Text style={styles.label}>Price</Text>
            <TextInput
              style={styles.input}
              value={price}
              onChangeText={setPrice}
              inputMode="decimal"
              keyboardType="numeric"
              placeholder="0.00"
            />

            {!isNotesVisible ? (
              <TouchableOpacity style={styles.addNotesButton} onPress={() => setIsNotesVisible(true)}>
                <MdAdd size={20} color="green" />
                <Text style={styles.addNotesText}>Add Notes</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity style={styles.hideNotesButton} onPress={() => setIsNotesVisible(false)}>
                  <MdRemove size={20} color="red" />
                  <Text style={styles.hideNotesText}>Hide Notes</Text>
                </TouchableOpacity>
                <View style={styles.fieldContainer}>
                  <Text style={styles.label}>Notes (for internal use only):</Text>
                  <TextInput
                    style={[styles.input, styles.textArea]}
                    placeholder="Enter notes here..."
                    multiline
                    numberOfLines={4}
                    onChangeText={setNotes}
                    value={notes}
                  />
                </View>
              </>
            )}

            <TouchableOpacity style={styles.saveButton} onPress={saveQuote}>
              <Text style={styles.saveButtonText}>Save Quote</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => navigate('/quotes', { replace: true })}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </TouchableWithoutFeedback>

      {/* Professional Quote Preview overlay */}
      {previewOpen && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <iframe
              srcDoc={`
                <!DOCTYPE html>
                <html>
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Quote Q-${lastCreatedQuoteId || 'XXXX'} - ${previewQuoteData.customerName || 'Customer'}</title>
                  <style>
                    @media print {
                      body { margin: 0; }
                      .action-buttons { display: none; }
                    }
                    
                    body {
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.6;
                      color: #333;
                      max-width: 800px;
                      margin: 0 auto;
                      padding: 60px 30px 100px 30px;
                      background: #fff;
                    }
                    
                    .header {
                      display: flex;
                      justify-content: space-between;
                      align-items: flex-start;
                      margin-bottom: 40px;
                      padding-bottom: 20px;
                      border-bottom: 3px solid #2563eb;
                      margin-top: 80px;
                    }
                    
                    .company-info {
                      flex: 1;
                    }
                    
                    .company-name {
                      font-size: 28px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .company-details {
                      color: #666;
                      font-size: 14px;
                    }
                    
                    .quote-info {
                      text-align: right;
                      flex: 1;
                    }
                    
                    .quote-title {
                      font-size: 32px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .quote-details {
                      font-size: 14px;
                      color: #666;
                    }
                    
                    .customer-section {
                      margin-bottom: 30px;
                    }
                    
                    .section-title {
                      font-size: 18px;
                      font-weight: bold;
                      color: #374151;
                      margin-bottom: 15px;
                      padding-bottom: 5px;
                      border-bottom: 2px solid #e5e7eb;
                    }
                    
                    .customer-info {
                      background: #f8fafc;
                      padding: 20px;
                      border-radius: 8px;
                      border-left: 4px solid #2563eb;
                    }
                    
                    .customer-name {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 10px;
                    }
                    
                    .services-section {
                      margin-bottom: 30px;
                    }
                    
                    .service-item {
                      background: #fff;
                      border: 1px solid #e5e7eb;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 20px;
                      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }
                    
                    .service-title {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 15px;
                    }
                    
                    .service-description {
                      color: #4b5563;
                      margin-bottom: 20px;
                      line-height: 1.7;
                      white-space: pre-wrap;
                    }
                    
                    .pricing-section {
                      background: #f8fafc;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 30px;
                      border: 2px solid #2563eb;
                    }
                    
                    .price-row {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 15px 0;
                      border-bottom: 1px solid #e5e7eb;
                    }
                    
                    .price-row:last-child {
                      border-bottom: none;
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-top: 10px;
                      padding-top: 20px;
                      border-top: 2px solid #2563eb;
                    }
                    
                    .price-label {
                      font-size: 18px;
                      color: #374151;
                    }
                    
                    .price-amount {
                      font-size: 18px;
                      font-weight: bold;
                      color: #1f2937;
                    }
                    
                    .total-label {
                      font-size: 20px;
                      font-weight: bold;
                    }
                    
                    .total-amount {
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                    }
                    
                    .terms-section {
                      margin-top: 40px;
                      padding: 20px;
                      background: #fefce8;
                      border-radius: 8px;
                      border-left: 4px solid #eab308;
                    }
                    
                    .terms-title {
                      font-size: 16px;
                      font-weight: bold;
                      color: #92400e;
                      margin-bottom: 10px;
                    }
                    
                    .terms-text {
                      font-size: 14px;
                      color: #a16207;
                      line-height: 1.6;
                    }
                    
                    .footer {
                      margin-top: 50px;
                      padding-top: 20px;
                      border-top: 2px solid #e5e7eb;
                      text-align: center;
                      color: #6b7280;
                      font-size: 14px;
                    }
                    
                    .action-buttons {
                      position: fixed;
                      top: 20px;
                      right: 20px;
                      display: flex;
                      flex-direction: row;
                      gap: 10px;
                      z-index: 1000;
                      background: rgba(255,255,255,0.95);
                      padding: 10px;
                      border-radius: 10px;
                      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    }
                    
                    .action-button {
                      background: #2563eb;
                      color: white;
                      border: none;
                      padding: 12px 20px;
                      border-radius: 8px;
                      font-size: 14px;
                      font-weight: bold;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
                      transition: all 0.2s;
                    }
                    
                    .action-button:hover {
                      background: #1d4ed8;
                      transform: translateY(-2px);
                      box-shadow: 0 4px 8px rgba(37, 99, 235, 0.4);
                    }
                    
                    .save-pdf-button {
                      background: #059669;
                    }
                    
                    .save-pdf-button:hover {
                      background: #047857;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <div class="company-info">
                      <div class="company-name">${businessInfo?.businessName || 'Your Business Name'}</div>
                      <div class="company-details">
                        ${(() => {
                          const bInfo = businessInfo || {};
                          const addressParts = [];
                          
                          if (bInfo.address) addressParts.push(bInfo.address);
                          if (bInfo.city && bInfo.state) {
                            const cityState = bInfo.zip ? `${bInfo.city}, ${bInfo.state} ${bInfo.zip}` : `${bInfo.city}, ${bInfo.state}`;
                            addressParts.push(cityState);
                          } else if (bInfo.city) {
                            addressParts.push(bInfo.city);
                          } else if (bInfo.state) {
                            addressParts.push(bInfo.state);
                          }
                          
                          if (bInfo.phone) {
                            addressParts.push(`Phone: ${bInfo.phone}`);
                          }
                          if (bInfo.email) {
                            addressParts.push(`Email: ${bInfo.email}`);
                          }
                          
                          // Add default contact info only if no business info available at all
                          if (addressParts.length === 0 && !bInfo.businessName) {
                            return `123 Business Street<br>City, State 12345<br>Phone: (555) 123-4567<br>Email: info@yourbusiness.com`;
                          }
                          
                          return addressParts.join('<br>') || 'Contact information not available';
                        })()}
                      </div>
                    </div>
                    <div class="quote-info">
                      <div class="quote-title">QUOTE</div>
                      <div class="quote-details">
                        <strong>Quote #:</strong> Q-${lastCreatedQuoteId || 'XXXX'}<br>
                        <strong>Date:</strong> ${new Date().toLocaleDateString()}<br>
                        <strong>Valid Until:</strong> ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div class="customer-section">
                    <div class="section-title">Quote For:</div>
                    <div class="customer-info">
                      <div class="customer-name">${previewQuoteData.customerName || 'Customer Name'}</div>
                      ${(() => {
                        const customer = customers.find(c => c.name === previewQuoteData.customerName);
                        if (customer) {
                          return `
                            ${customer.address ? `<div>${customer.address}</div>` : ''}
                            ${customer.city && customer.state ? `<div>${customer.city}, ${customer.state} ${customer.zip || ''}</div>` : ''}
                            ${customer.phone ? `<div>Phone: ${customer.phone}</div>` : ''}
                            ${customer.email ? `<div>Email: ${customer.email}</div>` : ''}
                          `;
                        }
                        return '';
                      })()}
                    </div>
                  </div>

                  <div class="services-section">
                    <div class="section-title">Services</div>
                    <div class="service-item">
                      <div class="service-title">${previewQuoteData.title || 'Service Title'}</div>
                      <div class="service-description">${previewQuoteData.description || 'Service description'}</div>
                    </div>
                  </div>

                  <div class="pricing-section">
                    <div class="price-row">
                      <span class="price-label">Service Total:</span>
                      <span class="price-amount">$${parseFloat(previewQuoteData.price || 0).toFixed(2)}</span>
                    </div>
                    <div class="price-row">
                      <span class="total-label">Total Amount:</span>
                      <span class="total-amount">$${parseFloat(previewQuoteData.price || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div class="terms-section">
                    <div class="terms-title">Quote Terms & Conditions</div>
                    <div class="terms-text">
                      • This quote is valid for 30 days from the date above<br>
                      • Prices are subject to change without notice<br>
                      • A 50% deposit may be required to begin work<br>
                      • Final pricing may vary based on actual work performed<br>
                      • Additional charges may apply for work outside the scope described above
                    </div>
                  </div>

                  <div class="footer">
                    <p>Thank you for considering our services. We look forward to working with you!</p>
                    <p><strong>Questions?</strong> Contact us${(() => {
                      const bInfo = businessInfo || {};
                      const contactParts = [];
                      
                      // Only add phone if it exists in business info
                      if (bInfo.phone) {
                        contactParts.push(`at ${bInfo.phone}`);
                      }
                      
                      // Only add email if it exists in business info  
                      if (bInfo.email) {
                        contactParts.push(`${contactParts.length > 0 ? 'or ' : 'at '}${bInfo.email}`);
                      }
                      
                      // Fallback to defaults if no contact info available
                      if (contactParts.length === 0) {
                        contactParts.push('at (555) 123-4567 or info@yourbusiness.com');
                      }
                      
                      return contactParts.length > 0 ? ' ' + contactParts.join(' ') : '';
                    })()}</p>
                  </div>

                  <div class="action-buttons">
                    <button class="action-button save-pdf-button" onclick="window.print()">
                      📄 Save as PDF
                    </button>
                  </div>
                </body>
                </html>
              `}
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Quote Preview"
            />
            
            <View style={styles.buttonGroup}>
              <TouchableOpacity 
                style={[styles.actionButton, styles.emailButton, sendingEmail && { opacity: 0.7 }]}
                onPress={async () => {
                  if (!lastCreatedQuoteId) {
                    showCustomModal(
                      'Error',
                      'Quote ID not available. Please try emailing from the quotes list.',
                      [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                    );
                    return;
                  }

                  const chosen = customers.find(
                    (c) => (c?.name ?? '').trim().toLowerCase() === (previewQuoteData.customerName || '').trim().toLowerCase()
                  );
                  
                  if (!chosen?.email) {
                    showCustomModal(
                      'Email Not Available ⚠️',
                      'Customer email not found. Please update customer information.',
                      [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                    );
                    return;
                  }

                  setPreviewOpen(false);
                  
                  try {
                    setSendingEmail(true);
                    
                    showCustomModal(
                      'Sending Email...',
                      'Please wait while we send your quote to the customer.',
                      [
                        {
                          text: 'Sending…',
                          style: 'primary',
                          disabled: true,
                          onPress: () => {}
                        }
                      ]
                    );
                    
                    const token = getToken();
                    const emailResponse = await axios.post(`${API_BASE_URL}/quotes/${lastCreatedQuoteId}/email`, {}, {
                      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    });

                    if (emailResponse.data && emailResponse.data.success) {
                      showCustomModal(
                        'Email Sent Successfully! ✅',
                        `Quote has been emailed as PDF to ${chosen.email}`,
                        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
                      );
                    } else {
                      showCustomModal(
                        'Email Status Unclear ⚠️',
                        emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
                        [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                      );
                    }
                  } catch (emailError) {
                    console.error('Error sending quote email:', emailError);
                    
                    let errorMessage = 'Failed to send email. Please try again.';
                    if (emailError.response?.status === 400) {
                      errorMessage = `Customer email issue. Please update customer information.`;
                    } else if (emailError.response?.data?.detail) {
                      errorMessage = emailError.response.data.detail;
                    }
                    
                    showCustomModal(
                      'Email Failed ❌',
                      errorMessage,
                      [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                    );
                  } finally {
                    setSendingEmail(false);
                  }
                }}
                disabled={sendingEmail}
              >
                <Text style={styles.actionButtonText}>
                  {sendingEmail ? 'Sending…' : '📧 Send to Customer'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity style={[styles.saveButton, { marginTop: 16 }]} onPress={() => {
                setPreviewOpen(false);
                // Show the three-option modal again when closing preview
                showCustomModal(
                  'Quote Created Successfully! 🎉',
                  'Your quote has been created. What would you like to do next?',
                  [
                    {
                      text: '📧 Email PDF to Customer',
                      style: 'primary',
                      onPress: async () => {
                        if (!lastCreatedQuoteId) {
                          showCustomModal(
                            'Error',
                            'Quote ID not available. Please try emailing from the quotes list.',
                            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                          );
                          return;
                        }

                        const chosen = customers.find(
                          (c) => (c?.name ?? '').trim().toLowerCase() === (previewQuoteData.customerName || '').trim().toLowerCase()
                        );
                        
                        if (!chosen?.email) {
                          showCustomModal(
                            'Email Not Available ⚠️',
                            'Customer email not found. Please update customer information.',
                            [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                          );
                          return;
                        }

                        try {
                          setSendingEmail(true);
                          
                          showCustomModal(
                            'Sending Email...',
                            'Please wait while we send your quote to the customer.',
                            [
                              {
                                text: 'Sending…',
                                style: 'primary',
                                disabled: true,
                                onPress: () => {}
                              }
                            ]
                          );
                          
                          const token = getToken();
                          const emailResponse = await axios.post(`${API_BASE_URL}/quotes/${lastCreatedQuoteId}/email`, {}, {
                            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                          });

                          if (emailResponse.data && emailResponse.data.success) {
                            showCustomModal(
                              'Email Sent Successfully! ✅',
                              `Quote has been emailed as PDF to ${chosen.email}`,
                              [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
                            );
                          } else {
                            showCustomModal(
                              'Email Status Unclear ⚠️',
                              emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
                              [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                            );
                          }
                        } catch (emailError) {
                          console.error('Error sending quote email:', emailError);
                          
                          let errorMessage = 'Failed to send email. Please try again.';
                          if (emailError.response?.status === 400) {
                            errorMessage = `Customer email issue. Please update customer information.`;
                          } else if (emailError.response?.data?.detail) {
                            errorMessage = emailError.response.data.detail;
                          }
                          
                          showCustomModal(
                            'Email Failed ❌',
                            errorMessage,
                            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                          );
                        } finally {
                          setSendingEmail(false);
                        }
                      }
                    },
                    {
                      text: '👁️ Preview Quote',
                      style: 'secondary',
                      onPress: () => {
                        closeCustomModal();
                        setPreviewOpen(true);
                      }
                    },
                    {
                      text: '📋 View Quotes',
                      style: 'default',
                      onPress: () => {
                        closeCustomModal();
                        navigate('/quotes', { replace: true });
                      }
                    }
                  ]
                );
              }}>
                <Text style={styles.saveButtonText}>Close Preview</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <Text style={styles.previewTitle}>{customModal.title}</Text>
            <Text>{customModal.message}</Text>
            <View style={styles.buttonGroup}>
              {customModal.buttons.map((button, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.actionButton,
                    button.style === 'primary' && { backgroundColor: 'blue' },
                    button.style === 'secondary' && { backgroundColor: 'gray' },
                    button.style === 'danger' && { backgroundColor: 'red' },
                    button.style === 'default' && { backgroundColor: '#f0f0f0' },
                  ]}
                  onPress={button.onPress}
                >
                  <Text style={[
                    styles.actionButtonText,
                    button.style === 'default' && { color: 'black' },
                    button.style !== 'default' && { color: 'white' }
                  ]}>
                    {button.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, paddingBottom: 50, backgroundColor: '#fff' },
  label: { marginTop: 15, fontWeight: 'bold' },
  input: { borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4 },
  textArea: { height: 100, textAlignVertical: 'top' },

  // search results
  searchResults: { marginTop: 5, backgroundColor: '#f9f9f9', borderRadius: 4, padding: 10 },
  searchResult: { paddingVertical: 5, fontSize: 14 },
  addCustomerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  addCustomerButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 10 },
  addCustomerText: { color: 'green', marginLeft: 5 },

  // buttons
  saveButton: { marginTop: 20, backgroundColor: 'blue', padding: 12, borderRadius: 6, alignItems: 'center' },
  saveButtonText: { color: 'white', fontWeight: 'bold' },
  cancelButton: { marginTop: 10, backgroundColor: 'red', padding: 12, borderRadius: 6, alignItems: 'center' },
  cancelButtonText: { color: 'white', fontWeight: 'bold' },

  // add-notes UI
  fieldContainer: { marginTop: 0 },
  addNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 15 },
  addNotesText: { color: 'green', marginLeft: 5 },
  hideNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 15 },
  hideNotesText: { color: 'red', marginLeft: 5 },

  // inline add-customer panel
  inlineModal: {
    marginTop: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  modalHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },
  modalLabel: { marginTop: 10, fontWeight: 'bold', fontSize: 14 },
  returnButton: { backgroundColor: '#ccc', padding: 10, borderRadius: 5, alignItems: 'center', marginTop: 10 },
  returnButtonText: { color: '#000', fontSize: 16, fontWeight: 'bold' },

  // preview overlay
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  previewCard: {
    width: 'min(640px, 100%)',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  bold: { fontWeight: '700' },
  buttonGroup: { marginTop: 16 },
  actionButton: { backgroundColor: '#f0f0f0', padding: 12, borderRadius: 6, alignItems: 'center', marginBottom: 10 },
  actionButtonText: { color: '#000', fontWeight: 'bold' },
  pdfButton: { backgroundColor: '#d3d3d3' },
  emailButton: { backgroundColor: '#d3d3d3' },
});
